/**
 * „Webinare der letzten 12 Monate" (P1-V3 § 2.3, Umbau 25.10.2026): ein Termin je Zeile, neueste oben (Ausnahme von
 * Regel 5), Erwarteter Wert (Summe) als Pflicht mit Anfos und Kontakten darunter, Anmeldungen, Show-up und Anfos je 100
 * Anmeldungen im Standard, der Rest über die Spaltenwahl. Darüber die Vergleichszeile „Typischer Termin" (Median der
 * fertigen Termine, Stil „Vergleich", nicht grau). Show-up kommt aus einem eigenen Abruf (fail-soft, nicht im Ranking,
 * das der Montags-Cron rechnet). Die Kandidaten-Karte oben liest dieselbe Ranking-Antwort (`fetchWebinarRankingGeteilt`).
 */
import { FC, ReactNode, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Tooltip } from '@mui/material';
import ChartCard from '../../../components/auswertungen/ChartCard';
import { BefundZeile } from '../../../components/auswertungen/kennzahlTeile';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import {
  BezahltBuendel,
  euroJeTausend,
  jeTausend,
  kompakt,
  nachGruppenReihenfolge,
  spanne,
  tEuro,
} from '../../../components/auswertungen/kennzahlTeile';
import { ATTRIBUTION, formatEur, formatPct, spaltenTip, TERMS } from '../../../helpers/kpiDefinitions';
import {
  fetchTeilnahme,
  fetchWebinarRankingGeteilt,
  ShowUpZeile,
  TypischerTermin,
  WebinarZeile,
} from '../../../helpers/auswertungenData';
import { formatNumber } from '../../../helpers/numberUtils';
import { abstandTypisch, MIN_ANFOS_WERT, topFlopBefund } from './befunde';
import { GeschaetztMarke, Hinweis, int, KURZ_GRUND, Laden, offenBeiTermin, Quote, SHOWUP_GRUND, Strich, tagMitWochentag, Unter, useLaden, ZahlenOffen, ZuordnungLaeuft } from './shared';

export { EmpfehlungKarte as KandidatenKarte } from './EmpfehlungKarte';

/** Grau-Schwellen der Karte (P1-V3 Regel 3); `MIN_ANFOS_WERT` (20) kommt aus `befunde.ts` (auch der Befund zählt erst ab da). */
const MIN_VOR_BEGINN = 50;
const MIN_ABMELDUNGEN = 20;

/** Zeile der Tabelle; die Vergleichszeile „Typischer Termin" trägt `typisch`. */
type Zeile = WebinarZeile & { typisch?: TypischerTermin; showUp?: ShowUpZeile };

const GRUPPEN = ['Wert', 'Anmeldung und Teilnahme', 'Menge', 'Zahlung (wächst mit dem Alter)', 'Abmeldungen'] as const;

// Persona-Umbau B4 (28.10.2026): „Erwarteter Wert je 1.000 Kontakte" ist Standard (breit, mittel und schmal) direkt neben
// dem Wert; „Anfos je 100 Anmeldungen" nur noch über die Spaltenwahl.
const STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['titel', 'datum', 'wert', 'w1k', 'anm', 'showup'],
  mittel: ['titel', 'datum', 'wert', 'w1k', 'anm', 'showup'],
  schmal: ['titel', 'wert', 'w1k'],
};

/** Zellgrund gegen den Typischen Termin (B4): heller Schleier, in beiden Modi lesbar; Pfeil in der Zelle trägt die Richtung. */
const GRUND_GUT = 'rgba(46, 125, 50, 0.13)';
const GRUND_SCHLECHT = 'rgba(198, 40, 40, 0.11)';

const VOR_TRENNUNG = 'Vor dem 25.08.2026 nicht getrennt gespeichert; ein kleiner Rest (unter 2 %) von Anmeldungen nach Beginn ist mitgezählt.';

const Titel: FC<{ r: Zeile; schmal: boolean }> = ({ r, schmal }) =>
  r.typisch ? (
    <Tooltip title={TERMS.typischerTermin.tip} arrow placement="top">
      {/* Eigener Block: sonst klebt der Text ohne Trennstelle am Etikett „Vergleich" (267 px lief über). */}
      <Box component="span" sx={{ fontWeight: 600, cursor: 'help', display: 'block' }}>
        {TERMS.typischerTermin.label} ({int(r.typisch.termine)} fertige Termine)
      </Box>
    </Tooltip>
  ) : (
    <Box sx={{ lineHeight: 1.3, maxWidth: 340 }}>
      <Link component={RouterLink} to={`/auswertungen/webinare/${r.webinar_id}`} underline="hover" sx={{ fontWeight: 600 }}>
        {r.public_title}
      </Link>
      <Box component="span" sx={{ display: 'block', fontSize: 12, color: 'text.secondary' }}>
        {r.plx_product ?? '–'}
        {schmal && ` · ${tagMitWochentag(r.start_date)}`}
      </Box>
    </Box>
  );

/** Show-up-Zelle: Quote mit „x von y", „—" mit Grund; grau unter 50 vor Beginn Angemeldeten. */
/** Paket F: Tooltip an der Anfo-Zahl, wenn Anfos über die Anmeldung (ohne Kennung) zugeordnet sind; sonst nur der Text. */
const AnfosOhneKennung: FC<{ n: number; children: ReactNode }> = ({ n, children }) =>
  n > 0 ? (
    <Tooltip
      title={`Davon ${int(n)} über die Anmeldung (ohne Kennung): kein Webinar-Code im Bestell-Link, aber dieselbe Adresse war vorher angemeldet und hat höchstens 72 Stunden nach Beginn bestellt.`}
      arrow
    >
      <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>{children}</span>
    </Tooltip>
  ) : (
    <>{children}</>
  );

const ShowUp: FC<{ s: ShowUpZeile | undefined; geladen: boolean }> = ({ s, geladen }) => {
  if (!s) return geladen ? <Strich grund="Für diesen Termin liegen keine Teilnahmedaten vor." /> : <>…</>;
  if (s.show_up === null)
    return (
      <Box sx={{ lineHeight: 1.25 }}>
        <Strich grund={s.grund ? SHOWUP_GRUND[s.grund] : 'Keine Teilnahmedaten.'} />
        {s.grund && <Unter>{KURZ_GRUND[s.grund]}</Unter>}
      </Box>
    );
  return (
    <Box sx={{ lineHeight: 1.25 }}>
      {formatPct(s.show_up)}
      <Unter>
        {int(s.attended_timely)} von {int(s.timely)}
      </Unter>
    </Box>
  );
};

export const RankingKarte: FC = () => {
  const { data, fehler } = useLaden(fetchWebinarRankingGeteilt);
  // Fail-soft: ohne Teilnahme bleiben die Show-up-Zellen „—", die Tabelle steht trotzdem.
  const teilnahme = useLaden(fetchTeilnahme);
  const showUpM = useMemo(() => new Map((teilnahme.data?.rows ?? []).map((s) => [s.webinar_id, s])), [teilnahme.data]);
  const tnGeladen = teilnahme.data !== null || teilnahme.fehler !== null;

  const rows: Zeile[] = useMemo(() => (data?.rows ?? []).map((r) => ({ ...r, showUp: showUpM.get(r.webinar_id) })), [data, showUpM]);
  const summe = useMemo(
    () => ({
      wert: rows.reduce((s, r) => s + (r.wert_summe_fest ?? 0), 0),
      anfos: rows.reduce((s, r) => s + r.anfos, 0),
    }),
    [rows],
  );
  const typischZeile: Zeile | null = useMemo(() => {
    const t = data?.typisch;
    if (!t || !data?.rows.length) return null;
    // Eine Zeilen-Hülle mit den Medianen; die Render-Funktionen lesen bei `typisch` nur diese Felder.
    return { ...data.rows[0], webinar_id: '__typisch', typisch: { ...t, show_up: teilnahme.data?.typisch_show_up ?? t.show_up } };
  }, [data, teilnahme.data]);

  const baue = (schmal: boolean, breit = true): Spalte<Zeile>[] => [
    {
      key: 'titel',
      label: 'Webinar',
      gruppe: 'Wert',
      pflicht: true,
      minWidth: breit ? 220 : undefined,
      render: (r) => <Titel r={r} schmal={schmal} />,
      sortValue: (r) => r.public_title,
    },
    {
      key: 'datum',
      label: 'Datum',
      gruppe: 'Wert',
      align: 'right',
      render: (r) => (r.typisch ? '' : tagMitWochentag(r.start_date)),
      sortValue: (r) => r.start_date,
    },
    {
      key: 'wert',
      label: TERMS.wertSumme.label,
      einheit: 'Summe · Anfos · Kontakte',
      tip: TERMS.wertSumme.tip,
      hinweis: 'Ohne vorläufig ergänzte Anfos. Darunter die Größe: Anfos des Termins (vorläufige mitgezählt und genannt, im Wert nicht enthalten) und Kontakte der Einladungen (ab 10.12.2025 gezählt, Beginn der Versanddaten).',
      gruppe: 'Wert',
      pflicht: true,
      align: 'right',
      unsicher: (r) => (r.is_young && !r.typisch ? 'Zahlen noch offen' : null),
      render: (r) =>
        r.typisch ? (
          <Box sx={{ lineHeight: 1.25 }}>
            {tEuro(r.typisch.wert_summe_fest)}
            <Unter>
              {int(r.typisch.anfos)} Anfos · {kompakt(r.typisch.contacts)} Kontakte
            </Unter>
          </Box>
        ) : (
          <Box sx={{ lineHeight: 1.25 }}>
            {tEuro(r.wert_summe_fest ?? null)}
            <Unter>
              <AnfosOhneKennung n={r.anfos_ohne_kennung ?? 0}>{int(r.anfos)} Anfos</AnfosOhneKennung>
              {r.provisional > 0 && ` (${int(r.provisional)} vorläufig)`} ·{' '}
              {r.contacts > 0 ? (
                `${kompakt(r.contacts)} Kontakte`
              ) : (
                <Tooltip title="Keine Kontaktzahl: Einladungen vor dem 10.12.2025 (Beginn der Versanddaten) oder Termin ohne eigene Webinar-Einladungen (nur über Newsletter oder Anzeigen beworben)." arrow>
                  <span style={{ cursor: 'help' }}>Kontakte —</span>
                </Tooltip>
              )}
            </Unter>
            <ZuordnungLaeuft n={offenBeiTermin(data?.zuordnung, r.webinar_id)} lauf={data?.zuordnung?.lauf} />
          </Box>
        ),
      sortValue: (r) => r.wert_summe_fest ?? null,
      summary: (
        <Box sx={{ lineHeight: 1.25 }}>
          {tEuro(summe.wert)}
          <Unter>{int(summe.anfos)} Anfos</Unter>
        </Box>
      ),
    },
    {
      key: 'w1k',
      label: TERMS.wertJe1kEinladung.label,
      einheit: TERMS.wertJe1kEinladung.einheit,
      tip: `${TERMS.wertJe1kEinladung.tip} Grün bzw. rot: mindestens 5 % über bzw. unter dem Typischen Termin.`,
      gruppe: 'Wert',
      align: 'right',
      unsicher: (r) => (!r.typisch && r.contacts > 0 && (r.is_young || (r.send_anfos_fest ?? r.send_anfos) < MIN_ANFOS_WERT) ? (r.is_young ? 'Zahlen noch offen' : `unter ${MIN_ANFOS_WERT} Anfos`) : null),
      render: (r) => {
        if (r.typisch) return euroJeTausend(r.typisch.value_per_1k);
        if (r.wert_je_1k_fest === undefined || r.wert_je_1k_fest === null)
          return <Strich grund="Nur Termine, deren Einladungen alle im Kontaktfenster liegen und mindestens 14 Tage alt sind." />;
        // Färbung nur bei sicheren Werten (Regel 4): graue Zellen bekommen keinen Grund.
        const sicher = !r.is_young && (r.send_anfos_fest ?? r.send_anfos) >= MIN_ANFOS_WERT;
        const ab = sicher ? abstandTypisch(r.wert_je_1k_fest, data?.typisch?.value_per_1k ?? null) : null;
        return (
          <Box
            sx={{
              lineHeight: 1.25,
              mx: -0.75,
              px: 0.75,
              borderRadius: '4px',
              backgroundColor: ab?.richtung === 'up' ? GRUND_GUT : ab?.richtung === 'down' ? GRUND_SCHLECHT : undefined,
            }}
            title={ab ? `${ab.text} gegen typisch ${euroJeTausend(data?.typisch?.value_per_1k ?? null)}` : undefined}
          >
            {ab && ab.richtung !== 'gleich' && (
              <Box component="span" aria-hidden sx={{ fontSize: 10, mr: 0.5 }}>
                {ab.richtung === 'up' ? '▲' : '▼'}
              </Box>
            )}
            {euroJeTausend(r.wert_je_1k_fest)}
            <Unter>aus {int(r.send_anfos_fest ?? r.send_anfos)} Anfos über die Einladung</Unter>
          </Box>
        );
      },
      sortValue: (r) => r.wert_je_1k_fest ?? null,
    },
    {
      key: 'anm',
      label: TERMS.anmeldungenLive.label,
      einheit: 'live',
      tip: TERMS.anmeldungenLive.tip,
      gruppe: 'Anmeldung und Teilnahme',
      align: 'right',
      render: (r) => {
        if (r.typisch) return int(r.typisch.registrations);
        const s = r.showUp;
        const getrennt = s ? s.too_late !== null : r.start_date.slice(0, 10) >= '2026-08-25';
        const inhalt = (
          <Box sx={{ lineHeight: 1.25 }}>
            {int(r.registrations)}
            {/* B26: die Eingestiegenen sichtbar (Touch hat keinen Tooltip) — sie erklären den Abstand zum Show-up-Nenner. */}
            <Unter>
              {getrennt ? `+${int(s?.too_late ?? r.registrations_after_start)} zu spät für live` : <Strich grund={VOR_TRENNUNG} />}
              {s && s.joined_live !== null && s.joined_live > 0 && ` · ${int(s.joined_live)} eingestiegen`}
            </Unter>
          </Box>
        );
        return s && s.joined_live !== null && s.timely !== null ? (
          <Tooltip
            title={`Davon ${int(s.joined_live)} während des Webinars eingestiegen. Der Show-up rechnet nur mit den ${int(s.timely)} vor Beginn Angemeldeten. ${TERMS.zuSpaetLive.tip}`}
            arrow
            placement="top"
          >
            <Box component="span" sx={{ cursor: 'help', display: 'inline-block' }}>
              {inhalt}
            </Box>
          </Tooltip>
        ) : (
          inhalt
        );
      },
      sortValue: (r) => r.registrations,
    },
    {
      key: 'showup',
      label: TERMS.showUpTermin.label,
      einheit: TERMS.showUpTermin.einheit,
      tip: TERMS.showUpTermin.tip,
      gruppe: 'Anmeldung und Teilnahme',
      align: 'right',
      unsicher: (r) => (!r.typisch && r.showUp && r.showUp.show_up !== null && (r.showUp.timely ?? 0) < MIN_VOR_BEGINN ? `unter ${MIN_VOR_BEGINN} vor Beginn` : null),
      render: (r) =>
        r.typisch ? (
          <Box sx={{ lineHeight: 1.25 }}>
            {formatPct(r.typisch.show_up)}
            {/* B56: der Median kommt aus weniger Terminen als die Zeile nennt — live dazuschreiben. */}
            {teilnahme.data?.typisch_termine != null && <Unter>aus {int(teilnahme.data.typisch_termine)} Terminen</Unter>}
          </Box>
        ) : (
          <ShowUp s={r.showUp} geladen={tnGeladen} />
        ),
      sortValue: (r) => r.showUp?.show_up ?? null,
    },
    {
      key: 'a100',
      label: TERMS.anfosJe100Anm.label,
      einheit: 'alle Anfos des Termins',
      tip: `${TERMS.anfosJe100Anm.tip} Ohne vorläufig ergänzte Anfos.`,
      gruppe: 'Anmeldung und Teilnahme',
      align: 'right',
      unsicher: (r) => (r.is_young && !r.typisch ? 'Zahlen noch offen' : null),
      render: (r) => {
        const v = r.typisch ? r.typisch.anfos_per_100_regs : r.anfos_per_100_regs;
        return v === null ? '–' : formatNumber(v, 1);
      },
      sortValue: (r) => r.anfos_per_100_regs,
    },
    {
      key: 'wja',
      label: 'Ø Erwarteter Wert je Anfo',
      tip: spaltenTip('wertBeiBestellung', `Mittel über die Anfos des Termins mit Wert, ohne vorläufige — vor allem ein Produkteffekt.`),
      gruppe: 'Wert',
      align: 'right',
      render: (r) =>
        r.typisch ? '' : formatEur(r.with_value_fest ? (r.wert_summe_fest ?? 0) / r.with_value_fest : null, 0),
      sortValue: (r) => (r.with_value_fest ? (r.wert_summe_fest ?? 0) / r.with_value_fest : null),
    },
    // Workshop A2 (29.10.2026): dieselbe Rechnung wie die Karte „Was eine Anmeldung wert ist", je Termin.
    {
      key: 'wjAnm',
      label: TERMS.wertJeAnmeldung.label,
      einheit: TERMS.wertJeAnmeldung.einheit,
      tip: `${TERMS.wertJeAnmeldung.tip} ${TERMS.vorhersageUngeprueft.tip}`,
      gruppe: 'Wert',
      align: 'right',
      unsicher: (r) => (r.typisch ? null : r.is_young ? 'Zahlen noch offen' : r.anfos - r.provisional < MIN_ANFOS_WERT ? `unter ${MIN_ANFOS_WERT} Anfos` : null),
      render: (r) => (r.typisch ? '' : r.registrations > 0 ? formatEur((r.wert_summe_fest ?? 0) / r.registrations, 2) : <Strich grund="Keine Live-Anmeldung." />),
      sortValue: (r) => (!r.typisch && r.registrations > 0 ? (r.wert_summe_fest ?? 0) / r.registrations : null),
    },
    {
      key: 'bjAnm',
      label: TERMS.budgetJeAnmeldung.label,
      einheit: TERMS.budgetJeAnmeldung.einheit,
      tip: TERMS.budgetJeAnmeldung.tip,
      gruppe: 'Wert',
      align: 'right',
      unsicher: (r) => (r.typisch ? null : r.is_young ? 'Zahlen noch offen' : r.anfos - r.provisional < MIN_ANFOS_WERT ? `unter ${MIN_ANFOS_WERT} Anfos` : null),
      render: (r) => (r.typisch ? '' : r.registrations > 0 ? formatEur((r.cpa_summe_fest ?? 0) / r.registrations, 2) : <Strich grund="Keine Live-Anmeldung." />),
      sortValue: (r) => (!r.typisch && r.registrations > 0 ? (r.cpa_summe_fest ?? 0) / r.registrations : null),
    },
    {
      key: 'teiln',
      label: TERMS.teilnehmer.label,
      tip: `${TERMS.teilnehmer.tip} Einschließlich der während des Webinars Eingestiegenen.`,
      gruppe: 'Anmeldung und Teilnahme',
      align: 'right',
      render: (r) => {
        if (r.typisch) return '';
        const s = r.showUp;
        if (!s || s.attendees === null) return '–';
        return (
          <Tooltip title={s.joined_live ? `inkl. ${int(s.joined_live)} während des Webinars eingestiegen` : ''} arrow placement="top">
            <span>{int(s.attendees)}</span>
          </Tooltip>
        );
      },
      sortValue: (r) => r.showUp?.attendees ?? null,
    },
    {
      key: 'anm1k',
      label: TERMS.anmeldungenJe1k.label,
      tip: 'Live-Anmeldungen des Termins je 1.000 Kontakte seiner Einladungen. Anmeldungen über Newsletter oder Anzeigen zählen im Zähler mit.',
      gruppe: 'Anmeldung und Teilnahme',
      align: 'right',
      render: (r) => (r.typisch ? '' : r.contacts > 0 ? jeTausend((r.registrations / r.contacts) * 1000) : '–'),
      sortValue: (r) => (r.contacts > 0 ? r.registrations / r.contacts : null),
    },
    {
      key: 'kontakte',
      label: TERMS.kontakteRegulaer.label,
      tip: TERMS.kontakteRegulaer.tip,
      gruppe: 'Menge',
      align: 'right',
      render: (r) => (r.typisch ? kompakt(r.typisch.contacts) : r.contacts > 0 ? int(r.contacts) : '–'),
      sortValue: (r) => (r.contacts > 0 ? r.contacts : null),
    },
    {
      key: 'sendAnfos',
      label: TERMS.anfosUeberEinladung.label,
      tip: TERMS.anfosUeberEinladung.tip,
      gruppe: 'Menge',
      align: 'right',
      render: (r) => (r.typisch ? '' : r.contacts > 0 ? int(r.send_anfos_fest ?? r.send_anfos) : '–'),
      sortValue: (r) => (r.contacts > 0 ? (r.send_anfos_fest ?? r.send_anfos) : null),
    },
    {
      key: 'abm',
      label: TERMS.abmeldungenJe1kVersand.label,
      tip: TERMS.abmeldungenJe1kVersand.tip,
      gruppe: 'Abmeldungen',
      align: 'right',
      unsicher: (r) => (!r.typisch && r.contacts > 0 && (r.unsubscribes ?? 0) < MIN_ABMELDUNGEN ? `unter ${MIN_ABMELDUNGEN} Abmeldungen` : null),
      render: (r) =>
        r.typisch ? (
          ''
        ) : r.unsubscribes_per_1k === null || r.unsubscribes_per_1k === undefined ? (
          '–'
        ) : (
          <Box sx={{ lineHeight: 1.25 }}>
            {jeTausend(r.unsubscribes_per_1k)}
            <Unter>
              {int(r.unsubscribes)} von {kompakt(r.contacts)}
            </Unter>
          </Box>
        ),
      sortValue: (r) => r.unsubscribes_per_1k ?? null,
    },
    {
      key: 'quote',
      label: TERMS.bezahlquote.label,
      einheit: 'Anfos ab 60 Tagen',
      tip: spaltenTip('bezahlquote', `Unter 20 reifen Anfos grau. Trennt je Termin kaum.`),
      gruppe: 'Zahlung (wächst mit dem Alter)',
      align: 'right',
      render: (r) => (r.typisch ? '' : <Quote rate={r.pay_rate} bezahlt={r.mature_paid} reif={r.mature_anfos} />),
      sortValue: (r) => r.pay_rate,
    },
    {
      key: 'paid',
      label: TERMS.umsatzNetto.label,
      einheit: 'bezahlte Anfos · Ø Alter',
      tip: TERMS.bezahltJeAnfo.tip,
      gruppe: 'Zahlung (wächst mit dem Alter)',
      // Kopplung (P1-V3 § 5): „bezahlt" nur, solange der Erwartete Wert sichtbar ist (Pflicht, also immer).
      nurMit: 'wert',
      align: 'right',
      render: (r) => (r.typisch ? '' : <BezahltBuendel wert={formatEur(r.paid_revenue, 0)} zahler={r.payers ?? 0} alterTage={r.avg_age_days ?? null} />),
      sortValue: (r) => r.paid_revenue,
    },
  ];
  const meta = nachGruppenReihenfolge(spaltenMeta(baue(false)), GRUPPEN);
  const wahl = useSpaltenwahl('webinare', meta, STANDARDS);
  const schmal = wahl.breite === 'schmal';
  const sichtbar = new Set(wahl.sichtbar);
  const spalten = baue(schmal, wahl.breite === 'breit').filter((s) => sichtbar.has(s.key));

  return (
    <ChartCard
      title="Webinare der letzten 12 Monate"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead="Jede Zeile ein Termin, die neuesten oben. Neben der Summe steht, was 1.000 Kontakte der Einladung gebracht haben — grün bzw. rot gegen den Typischen Termin; die Summe allein lässt einen Termin an doppelt so vielen Kontakten besser aussehen. Grau heißt: noch nicht sicher. Ein Klick auf den Titel öffnet die Detailseite."
      terms={[
        { label: ATTRIBUTION.webinar.label, tip: ATTRIBUTION.webinar.tip },
        { label: TERMS.wertSumme.label, tip: TERMS.wertSumme.tip },
        { label: TERMS.showUpTermin.label, tip: TERMS.showUpTermin.tip },
        { label: TERMS.zuSpaetLive.label, tip: TERMS.zuSpaetLive.tip },
        { label: TERMS.eingestiegen.label, tip: TERMS.eingestiegen.tip },
        { label: TERMS.zahlenOffen.label, tip: TERMS.zahlenOffen.tip },
        { label: TERMS.typischerTermin.label, tip: TERMS.typischerTermin.tip },
      ]}
      source="Webinar-Anmeldungen und Teilnahme, Webinar-Zuordnung der Bestellungen, Webinar-Versände, Anfo-Wert je Anfo"
      freshnessKey={['plx_orders', 'webinar_zuordnung', 'webinar_send_metrics', 'anfo_werte']}
    >
      <Box ref={wahl.messRef}>
        {!data ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            {/* B4: Top 3 und Flop 3 je 1.000 Kontakte, nur Termine mit Kontaktzahl und fertigem, sicherem Wert. */}
            <BefundZeile
              text={topFlopBefund(rows, data.typisch)}
              fehlt={topFlopBefund(rows, data.typisch) ? null : 'Zu wenige Termine mit fertigem Wert je 1.000 Kontakte für eine Rangfolge.'}
              tip={TERMS.wertJe1kEinladung.tip}
            />
            <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
              Termine {spanne(data.window.von, data.window.asOf)} · {int(rows.length)} Termine
              {teilnahme.fehler && ' · Teilnahme konnte nicht geladen werden, Show-up fehlt'}
            </Box>
            <SortableTable
              rows={rows}
              columns={spalten}
              rowKey={(r) => r.webinar_id}
              initialSort={{ key: 'datum', dir: 'desc' }}
              maxHeight={null}
              kopfKlebtSeite={wahl.breite === 'breit'}
              eng={wahl.breite !== 'breit'}
              vergleichsZeilen={typischZeile ? [typischZeile] : undefined}
              namensEtikett={(r) =>
                r.typisch ? null : (
                  <>
                    <ZahlenOffen bis={r.zahlen_offen_bis} />
                    <GeschaetztMarke anfos={r.wert_geschaetzt_anfos} />
                  </>
                )
              }
              summaryLabel={`Alle ${int(rows.length)} Termine`}
            />
            <Hinweis>
              Euro netto. Grau = noch nicht sicher (Zahlen noch offen; Show-up unter {MIN_VOR_BEGINN} vor Beginn Angemeldeten; Wert je 1.000 unter{' '}
              {MIN_ANFOS_WERT} Anfos über die Einladung; Abmeldungen unter {MIN_ABMELDUNGEN}). — = nicht messbar, der Grund steht im Tooltip. Ohne
              Dauer-Webinare.
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};
